/**
 * VideoEntryCard Component
 *
 * Individual video entry card for release post generator.
 * Supports both auto-extraction and manual transcript paste.
 *
 * Story: RELEASE-002 - Multi-Video Support & Manual Transcript Input
 */
import { useState } from 'react'
import { Card } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Textarea } from '@/components/ui/textarea'
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group'
import { Loader2, Video, Trash2 } from 'lucide-react'
import type { VideoEntry } from '@/schemas/releasePost.schema'

interface VideoEntryCardProps {
  video: VideoEntry
  index: number
  canRemove: boolean
  onUpdate: (video: VideoEntry) => void
  onRemove: () => void
  onExtract: () => void
  isExtracting: boolean
}

export function VideoEntryCard({
  video,
  index,
  canRemove,
  onUpdate,
  onRemove,
  onExtract,
  isExtracting,
}: VideoEntryCardProps) {
  const [inputMethod, setInputMethod] = useState<'auto_extract' | 'manual_paste'>(
    video.input_method || 'auto_extract'
  )

  const handleMethodChange = (method: 'auto_extract' | 'manual_paste') => {
    setInputMethod(method)
    onUpdate({ ...video, input_method: method })
  }

  return (
    <Card className="p-4 bg-card border-border">
      <div className="flex items-center justify-between mb-4">
        <h3 className="font-semibold text-foreground">Video {index + 1}</h3>
        {canRemove && (
          <Button
            type="button"
            variant="ghost"
            size="sm"
            onClick={onRemove}
            className="text-destructive hover:bg-destructive/10"
          >
            <Trash2 className="h-4 w-4" />
          </Button>
        )}
      </div>

      {/* Input Method Toggle */}
      <div className="space-y-2 mb-4">
        <Label className="text-foreground">Input Method</Label>
        <RadioGroup
          value={inputMethod}
          onValueChange={handleMethodChange}
          className="flex gap-4"
        >
          <div className="flex items-center space-x-2">
            <RadioGroupItem value="auto_extract" id={`auto-${index}`} />
            <Label htmlFor={`auto-${index}`} className="text-foreground cursor-pointer">
              Auto-extract from Loom
            </Label>
          </div>
          <div className="flex items-center space-x-2">
            <RadioGroupItem value="manual_paste" id={`manual-${index}`} />
            <Label htmlFor={`manual-${index}`} className="text-foreground cursor-pointer">
              Paste transcript manually
            </Label>
          </div>
        </RadioGroup>
      </div>

      {/* Conditional Fields Based on Input Method */}
      {inputMethod === 'auto_extract' ? (
        <>
          <div className="space-y-2 mb-4">
            <Label htmlFor={`loom-url-${index}`} className="text-foreground">
              Loom Video URL
            </Label>
            <div className="flex gap-2">
              <Input
                id={`loom-url-${index}`}
                placeholder="https://www.loom.com/share/..."
                value={video.loom_url || ''}
                onChange={(e) => onUpdate({ ...video, loom_url: e.target.value })}
                className="bg-background border-border text-foreground flex-1"
              />
              <Button
                type="button"
                onClick={onExtract}
                disabled={isExtracting || !video.loom_url}
                className="gap-2"
              >
                {isExtracting ? (
                  <Loader2 className="h-4 w-4 animate-spin" />
                ) : (
                  <Video className="h-4 w-4" />
                )}
                Extract
              </Button>
            </div>
          </div>

          {video.transcript && (
            <div className="space-y-2 mb-4">
              <Label className="text-foreground">Extracted Transcript (Preview)</Label>
              <Textarea
                value={video.transcript}
                readOnly
                className="bg-muted border-border text-muted-foreground min-h-[100px]"
              />
            </div>
          )}
        </>
      ) : (
        <div className="space-y-2 mb-4">
          <Label htmlFor={`transcript-${index}`} className="text-foreground">
            Paste Transcript
          </Label>
          <Textarea
            id={`transcript-${index}`}
            placeholder="Paste the video transcript here..."
            value={video.transcript || ''}
            onChange={(e) => onUpdate({ ...video, transcript: e.target.value })}
            className="bg-background border-border text-foreground min-h-[150px]"
          />
          <p className="text-xs text-muted-foreground">
            Paste the transcript text from your Loom video or any demo recording
          </p>

          <div className="space-y-2 mt-4">
            <Label htmlFor={`loom-url-optional-${index}`} className="text-foreground">
              Loom URL (Optional - for embedding)
            </Label>
            <Input
              id={`loom-url-optional-${index}`}
              placeholder="https://www.loom.com/share/... (optional)"
              value={video.loom_url || ''}
              onChange={(e) => onUpdate({ ...video, loom_url: e.target.value })}
              className="bg-background border-border text-foreground"
            />
          </div>
        </div>
      )}

      {/* Optional Metadata */}
      <div className="space-y-4">
        <div className="space-y-2">
          <Label htmlFor={`title-${index}`} className="text-foreground">
            Video Title (Optional)
          </Label>
          <Input
            id={`title-${index}`}
            placeholder="e.g., Admin UI Walkthrough"
            value={video.title || ''}
            onChange={(e) => onUpdate({ ...video, title: e.target.value })}
            className="bg-background border-border text-foreground"
          />
        </div>

        <div className="space-y-2">
          <Label htmlFor={`description-${index}`} className="text-foreground">
            Description (Optional)
          </Label>
          <Input
            id={`description-${index}`}
            placeholder="e.g., Overview of new admin features"
            value={video.description || ''}
            onChange={(e) => onUpdate({ ...video, description: e.target.value })}
            className="bg-background border-border text-foreground"
          />
        </div>
      </div>
    </Card>
  )
}
